import React, { useState, useEffect } from 'react';
import { ChevronLeft, Lock } from 'lucide-react';
import { useAppContext } from '../../core/App';
import { useTelemetry } from '../../core/hooks/useTelemetry';
import { 
  initialBudgetNeeds, 
  initialBudgetWants, 
  initialBudgetSavings 
} from '../../core/constants/initialStates';

import BudgetStep1 from './components/BudgetStep1';
import BudgetStep2 from './components/BudgetStep2';
import BudgetStep3 from './components/BudgetStep3';
import BudgetStep4 from './components/BudgetStep4';
import BudgetStep5 from './components/BudgetStep5';
import ResilienceEngine from '../../components/shared/ResilienceEngine';

const BudgetSimulatorInner = ({ onComplete, isReviewMode }) => {
  const { 
    authUser,
    goToDashboard, 
    budgetData,       
    updateBudgetData,
    logActivity,
    setToastMessage
  } = useAppContext();
  
  const { transmitTelemetry } = useTelemetry();

  const [currentStep, setCurrentStep] = useState(isReviewMode ? 5 : 1);
  const [budgetMode, setBudgetMode] = useState('standard'); 

  const [jobZip, setJobZip] = useState(budgetData.jobZip || '');
  const [homeZip, setHomeZip] = useState(budgetData.homeZip || '');
  const [commuteMode, setCommuteMode] = useState(budgetData.commuteMode || 'transit');
  const [takeHome, setTakeHome] = useState(budgetData.takeHome || 0);
  const [needs, setNeeds] = useState(budgetData.needs || initialBudgetNeeds);
  const [wants, setWants] = useState(budgetData.wants || initialBudgetWants);
  const [savings, setSavings] = useState(budgetData.savings || initialBudgetSavings);

  // Behavioral Telemetry States
  const [expectationDelta, setExpectationDelta] = useState(0);
  const [iterationCount, setIterationCount] = useState(0);
  const [baselineWants, setBaselineWants] = useState(null);
  const [baselineSavings, setBaselineSavings] = useState(null);

  const location = homeZip || 'default';

  useEffect(() => {
    if (isReviewMode) {
      setCurrentStep(5);
      setJobZip(budgetData.jobZip || '');
      setHomeZip(budgetData.homeZip || '');
      setCommuteMode(budgetData.commuteMode || 'transit');
      setTakeHome(budgetData.takeHome || 0);
      setNeeds(budgetData.needs || initialBudgetNeeds);
      setWants(budgetData.wants || initialBudgetWants);
      setSavings(budgetData.savings || initialBudgetSavings);
    }
  }, [isReviewMode, budgetData]); 

  useEffect(() => {
    if (!isReviewMode) {
      const newData = { jobZip, homeZip, commuteMode, takeHome, needs, wants, savings };
      if (JSON.stringify(newData) !== JSON.stringify(budgetData)) {
         const timer = setTimeout(() => {
            if (authUser) updateBudgetData(newData);
         }, 500); 
         return () => clearTimeout(timer);
      }
    }
  }, [jobZip, homeZip, commuteMode, takeHome, needs, wants, savings, isReviewMode, budgetData, updateBudgetData, authUser]);

  const getAvgRent = (zip) => {
      if (!zip) return 1500;
      const prefix = zip.substring(0, 2);
      if (prefix === '10' || prefix === '11') return 3500; 
      if (prefix === '90' || prefix === '91' || prefix === '92' || prefix === '93' || prefix === '94') return 2800; 
      if (prefix === '77' || prefix === '78') return 1400; 
      if (prefix === '33' || prefix === '34') return 1800; 
      return 1500;
  };

  const avgRent = getAvgRent(homeZip);
  const isSurvivalZone = takeHome > 0 && (avgRent / takeHome) > 0.35;

  // Baseline Capture for the Sacrifice Logic
  useEffect(() => {
    if (currentStep === 5 && isSurvivalZone && budgetMode !== 'hcol' && baselineWants === null) {
      const totalN = Object.values(needs).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
      const totalW = Object.values(wants).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
      const totalS = Object.values(savings).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
      const leftToBudget = takeHome - (totalN + totalW + totalS);

      if (leftToBudget < 0) {
        setBaselineWants(totalW);
        setBaselineSavings(totalS);
      }
    }
  }, [currentStep, isSurvivalZone, budgetMode, needs, wants, savings, takeHome, baselineWants]);

  // CRITICAL PATCH: Removed artificial B2C gating. Progression is now strictly math-based.
  const handleAttemptNext = () => {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
  };

  const handlePrev = () => setCurrentStep((prev) => Math.max(prev - 1, 1));
  
  const handleChange = (setter, field, value) => {
    setter((prev) => ({ ...prev, [field]: value }));
    if (isSurvivalZone && (currentStep === 3 || currentStep === 4)) {
      setIterationCount((prev) => prev + 1);
    }
  };

  const handleCompleteSimulator = async (payload) => {
    if (isReviewMode) { onComplete(); return; }

    const totalNeeds = Object.values(needs).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
    const currentWants = Object.values(wants).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
    const currentSavings = Object.values(savings).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);

    const currentNeedsPercent = takeHome > 0 ? (totalNeeds / takeHome) : 0;
    const isStabilized = isSurvivalZone ? (currentNeedsPercent <= 0.70) : (currentNeedsPercent <= 0.50);
    const rentRatio = takeHome > 0 ? (avgRent / takeHome) : 0;

    if (authUser) {
      // CSO DIRECTIVE: Strict PII Stripping & Zero-Trust Architecture. 
      // Transmit ONLY raw behavioral metrics. Algorithmic execution moved to backend.
      const telemetryPayload = {
        moduleId: isSurvivalZone && isStabilized ? 'COMPLETE_SURVIVAL_BUDGET' : 'COMPLETE_BUDGET_SIM',
        rawInteractions: [
          {
            status: isStabilized ? 'success' : 'failed',
            isSurvivalZone,
            rentToIncomeRatio: rentRatio, 
            budgetMode,
            
            // Raw telemetry array for backend computation
            expectationDelta,
            iterationCount,
            takeHome,
            baselineWants: baselineWants || 0,
            currentWants,
            baselineSavings: baselineSavings || 0,
            currentSavings
          }
        ]
      };
      await transmitTelemetry(telemetryPayload);

      // Keep local activity log for UX, but do not send raw dollars to global metrics
      await logActivity('COMPLETE_BUDGET_SIM', { 
          isSurvivalZone,
          isStabilized,
          rentToIncomeRatio: rentRatio,
          jobZip,
          homeZip,
          commuteMode,
          budgetMode,
          totalSaved: currentSavings,
          reflection: payload?.reflection || null 
      });
    }
    
    onComplete();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1: 
        return <BudgetStep1 jobZip={jobZip} setJobZip={setJobZip} homeZip={homeZip} setHomeZip={setHomeZip} commuteMode={commuteMode} setCommuteMode={setCommuteMode} takeHome={takeHome} setTakeHome={setTakeHome} onNext={handleAttemptNext} setExpectationDelta={setExpectationDelta} />;
      case 2: 
        return <BudgetStep2 needs={needs} setNeeds={setNeeds} handleChange={handleChange} onNext={handleAttemptNext} onPrev={handlePrev} isSurvivalZone={isSurvivalZone} location={location} avgRent={avgRent} takeHome={takeHome} />;
      case 3: 
        return <BudgetStep3 wants={wants} setWants={setWants} handleChange={handleChange} onNext={handleAttemptNext} onPrev={handlePrev} />;
      case 4: 
        return <BudgetStep4 savings={savings} setSavings={setSavings} handleChange={handleChange} onNext={handleAttemptNext} onPrev={handlePrev} />;
      case 5: 
        return <BudgetStep5 authUser={authUser} takeHome={takeHome} needs={needs} wants={wants} savings={savings} onPrev={handlePrev} onComplete={handleCompleteSimulator} isReviewMode={isReviewMode} budgetMode={budgetMode} setBudgetMode={setBudgetMode} isSurvivalZone={isSurvivalZone} location={location} avgRent={avgRent} />;
      default: return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden animate-fade-in pb-20 relative">
      <div className="p-6 sm:p-8 relative">
        <button onClick={goToDashboard} className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 mb-4">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </button>

        <div className="mb-6">
          <div className="flex justify-between text-sm font-medium text-gray-600 mb-1">
            <span>Step {currentStep} of 5</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-in-out" style={{ width: `${(currentStep / 5) * 100}%` }}></div>
          </div>
        </div>

        <div className="relative">
           {renderStep()}
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 bg-gray-50 border-t border-gray-200 p-3 text-center flex flex-col items-center">
         <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Educational Simulator Only • Not Financial Advice • No Real Money Connected</p>
         <p className="text-[10px] text-blue-500 uppercase font-bold tracking-wider mt-1">Calibrated for 2026 IRS Tax Brackets & Standard Deduction Limits</p>
      </div>
    </div>
  );
};

const BudgetSimulator = ({ onComplete, isReviewMode }) => {
    if (isReviewMode) {
        return <BudgetSimulatorInner onComplete={onComplete} isReviewMode={true} />;
    }
    return (
        <ResilienceEngine onModuleComplete={onComplete} moduleName="Macroeconomic Strain Simulator">
            <BudgetSimulatorInner isReviewMode={false} />
        </ResilienceEngine>
    );
};

export default BudgetSimulator;